import type React from 'react';
import {useRef, useState} from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import {Close, SellOutlined, UploadFileOutlined} from '@mui/icons-material';
import {suggestCriterion} from '../../domain/raqsci';
import type {CategoryRecord, RaqsciCode, Requirement, RequirementInput} from '../../types/categoryRecordTypes';

type Props = {
    criteria: CategoryRecord['options']['criteria'];
    attachmentTypes: CategoryRecord['options']['attachmentTypes'];
    /** Edit this requirement; create a new one when absent. */
    requirement?: Requirement;
    onSubmit: (input: RequirementInput, file: File | null, removeAttachment: boolean) => Promise<unknown>;
    onClose: () => void;
};

/** Add / edit a business requirement (spec 4.4). Mount it to open it. */
export const RequirementDialog: React.FC<Props> = ({criteria, attachmentTypes, requirement, onSubmit, onClose}) => {
    const [input, setInput] = useState<RequirementInput>({
        label: requirement?.label ?? '', statement: requirement?.statement ?? '', criterion: requirement?.criterion ?? 'Q',
    });
    const [file, setFile] = useState<File | null>(null);
    const [removeAttachment, setRemoveAttachment] = useState(false);
    const [fileError, setFileError] = useState('');
    const [suggested, setSuggested] = useState<string | null>(null);
    const [dragging, setDragging] = useState(false);
    const [busy, setBusy] = useState(false);
    const fileInput = useRef<HTMLInputElement>(null);
    const kept = requirement?.attachment && !removeAttachment && !file ? requirement.attachment : null;

    const pick = (f: File | undefined) => {
        if (!f) return;
        const ext = `.${f.name.split('.').pop()?.toLowerCase()}`;
        if (!attachmentTypes.includes(ext)) return setFileError(`Use one of these file types: ${attachmentTypes.join(', ')}.`);
        setFileError('');
        setFile(f);
    };

    const suggest = () => {
        const code = suggestCriterion(`${input.label} ${input.statement}`);
        if (code) setInput({...input, criterion: code});
        setSuggested(code ? `Suggested ${criteria.find(c => c.code === code)?.label} from the text.`
            : 'No criterion recognised in the text.');
    };

    const submit = () => {
        setBusy(true);
        onSubmit({...input, label: input.label.trim(), statement: input.statement.trim()}, file, removeAttachment)
            .then(onClose)
            .catch(() => setBusy(false));
    };

    return (
        <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="cp-req-title">
            <DialogTitle id="cp-req-title">{requirement ? 'Edit requirement' : 'Add requirement'}</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{pt: 1}}>
                    <TextField label="Requirement" required autoFocus size="small" value={input.label}
                               onChange={e => setInput({...input, label: e.target.value})}
                               slotProps={{htmlInput: {maxLength: 200}}}/>
                    <TextField label="Statement" multiline minRows={3} size="small" value={input.statement}
                               onChange={e => setInput({...input, statement: e.target.value})}
                               slotProps={{htmlInput: {maxLength: 5000}}}/>
                    <Stack direction="row" gap={1} alignItems="flex-start">
                        <TextField select label="RAQSCI criterion" size="small" sx={{flex: 1}} value={input.criterion}
                                   helperText={suggested ?? ' '}
                                   onChange={e => setInput({...input, criterion: e.target.value as RaqsciCode})}>
                            {criteria.map(c => <MenuItem key={c.code} value={c.code}>{`${c.code} · ${c.label}`}</MenuItem>)}
                        </TextField>
                        <Button startIcon={<SellOutlined/>} onClick={suggest} sx={{mt: 0.25}}
                                disabled={!input.label.trim() && !input.statement.trim()}>
                            Suggest from the text
                        </Button>
                    </Stack>

                    <Box
                        role="button" tabIndex={0} aria-label="Attachment: drop a file or click to choose"
                        onClick={() => fileInput.current?.click()}
                        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && fileInput.current?.click()}
                        onDragOver={e => {
                            e.preventDefault();
                            setDragging(true);
                        }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={e => {
                            e.preventDefault();
                            setDragging(false);
                            pick(e.dataTransfer.files[0]);
                        }}
                        sx={{
                            border: '1px dashed', borderColor: dragging ? 'primary.main' : 'divider', borderRadius: 1,
                            p: 2, textAlign: 'center', cursor: 'pointer', bgcolor: dragging ? 'action.hover' : undefined,
                        }}
                    >
                        <UploadFileOutlined color="action"/>
                        <Typography variant="body2">Attachment: drop a file here or click to choose</Typography>
                        <Typography variant="caption" color="text.secondary">PDF, Office, CSV, text or image</Typography>
                        <input ref={fileInput} type="file" hidden accept={attachmentTypes.join(',')} onChange={e => pick(e.target.files?.[0])}/>
                    </Box>
                    {fileError && <Typography variant="caption" color="error.main">{fileError}</Typography>}
                    {(file || kept) && (
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Typography variant="body2" sx={{flex: 1}} noWrap>{file?.name ?? kept?.name}</Typography>
                            <IconButton size="small" aria-label="Remove the attachment" onClick={() => {
                                if (file) setFile(null);
                                else setRemoveAttachment(true);
                            }}>
                                <Close fontSize="small"/>
                            </IconButton>
                        </Stack>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={busy || !input.label.trim()}>
                    {requirement ? 'Save' : 'Add requirement'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
